import { computed, effect, inject, Injectable, resource, signal } from '@angular/core';
import { INTEGRATIONS_PATH } from '../constants';
import { IntegrationCategoryEnum } from '../enums';
import { IntegrationModelRequestDto, IntegrationModelResponseDto } from '../models';
import { ApiService } from './api.service';
import { VenueService } from './venue.service';

/**
 * Predefined integration catalog - source of truth for available integrations
 * The backend only stores enabled integrations, the frontend merges with this catalog
 */
export interface IntegrationCatalogItem {
  integrationId: string;
  name: string;
  description: string;
  iconUrl: string;
  category: IntegrationCategoryEnum;
  defaultSettings?: Record<string, unknown>;
}

/**
 * Integration catalog - defines available integrations
 * Default settings must match backend API defaults from INTEGRATIONS_BACKEND_IMPLEMENTATION.md
 */
export const INTEGRATION_CATALOG: IntegrationCatalogItem[] = [
  {
    integrationId: 'printers',
    name: 'Printers',
    description: 'Configure thermal receipt printers for orders, kitchen tickets, and reports.',
    iconUrl: 'printer',
    category: IntegrationCategoryEnum.HARDWARE,
    defaultSettings: {
      printers: [],
      defaultPrinters: {},
      enableTicketsPrinting: false,
      printOnOrderConfirm: false,
      autoCut: true,
      openCashDrawer: false,
    },
  },
  // Future integrations can be added here:
  // {
  //   integrationId: 'fiscal-printer',
  //   name: 'Fiscal Printer',
  //   description: 'Romanian fiscal printer integration for receipts and reports.',
  //   iconUrl: 'file-done',
  //   category: IntegrationCategoryEnum.HARDWARE,
  // },
];

@Injectable({ providedIn: 'root' })
export class IntegrationsService {
  readonly #apiService = inject(ApiService<IntegrationModelRequestDto, IntegrationModelResponseDto>);
  readonly #venueService = inject(VenueService);

  // Trigger for loading (incremented to force reload)
  readonly #loadTrigger = signal<number>(0);

  // Resource for fetching enabled integrations from backend
  readonly integrationsResource = resource({
    params: () => ({
      trigger: this.#loadTrigger(),
      venueId: this.#venueService.selectedVenueId(),
    }),
    loader: async ({ params }) => {
      const { venueId } = params;
      if (!venueId) {
        return [];
      }

      try {
        // Fetch only enabled integrations from the API
        const enabledIntegrations = await this.#apiService.getAll(`venues/${venueId}/integrations`);
        return enabledIntegrations;
      } catch {
        // If endpoint returns 404 or error, return empty (no integrations configured yet)
        return [];
      }
    },
  });

  // Private signal sources
  readonly #enabledIntegrationsSource = signal<IntegrationModelResponseDto[]>([]);
  readonly #activeFilterSource = signal<'all' | IntegrationCategoryEnum>('all');

  // Public readonly signals
  readonly loading = this.integrationsResource.isLoading;
  readonly error = computed(() => (this.integrationsResource.error() ? 'Failed to load integrations' : null));
  readonly activeFilter = this.#activeFilterSource.asReadonly();

  /**
   * Merged integrations: catalog items + backend state
   * Always shows ALL available integrations from catalog, with enabled state from backend
   */
  readonly integrations = computed(() => {
    const venueId = this.#venueService.selectedVenueId();
    const enabledIntegrations = this.#enabledIntegrationsSource();

    return INTEGRATION_CATALOG.map((catalogItem) => {
      // Find if this integration is enabled in backend
      const backendRecord = enabledIntegrations.find((i) => i.integrationId === catalogItem.integrationId);

      // Always use catalog data for display (name, description, icon, category)
      // Merge with backend data for state (_id, isEnabled, settings, timestamps)
      return {
        _id: backendRecord?._id || '', // Empty if not yet persisted
        venue: backendRecord?.venue || venueId || '',
        integrationId: catalogItem.integrationId,
        name: catalogItem.name,
        description: catalogItem.description,
        iconUrl: catalogItem.iconUrl,
        category: catalogItem.category,
        isEnabled: backendRecord?.isEnabled ?? false,
        settings: backendRecord?.settings || catalogItem.defaultSettings || {},
        createdAt: backendRecord?.createdAt,
        updatedAt: backendRecord?.updatedAt,
      } as IntegrationModelResponseDto;
    });
  });

  // Computed signals
  readonly filteredIntegrations = computed(() => {
    const integrations = this.integrations();
    const filter = this.#activeFilterSource();

    if (filter === 'all') {
      return integrations;
    }

    return integrations.filter((integration) => integration.category === filter);
  });

  readonly integrationsCount = computed(() => this.integrations().length);
  readonly enabledIntegrationsCount = computed(() => this.integrations().filter((integration) => integration.isEnabled).length);

  constructor() {
    // Sync resource data to internal signal when it loads
    effect(() => {
      const data = this.integrationsResource.value();
      if (data !== undefined) {
        this.#enabledIntegrationsSource.set(data);
      }
    });
  }

  /**
   * Trigger a reload of integrations
   */
  loadIntegrations(): void {
    this.#loadTrigger.update((v) => v + 1);
  }

  /**
   * Set the active filter for integrations
   */
  setFilter(filter: 'all' | IntegrationCategoryEnum): void {
    this.#activeFilterSource.set(filter);
  }

  /**
   * Get all integrations for the current venue (merged catalog + backend)
   */
  async getAllIntegrations(): Promise<IntegrationModelResponseDto[]> {
    return Promise.resolve(this.integrations());
  }

  /**
   * Toggle an integration's enabled status
   * Uses PATCH /venues/:venueId/integrations/:integrationId which upserts if not exists
   * @see docs/INTEGRATIONS_BACKEND_IMPLEMENTATION.md for API contract
   */
  async toggleIntegrationStatus(integrationIdOrMongoId: string): Promise<IntegrationModelResponseDto> {
    const currentIntegrations = this.integrations();
    // Find by either _id (MongoDB ObjectId) or integrationId (catalog key)
    const integration = currentIntegrations.find((i) => i._id === integrationIdOrMongoId || i.integrationId === integrationIdOrMongoId);

    if (!integration) {
      throw new Error(`Integration with id ${integrationIdOrMongoId} not found`);
    }

    const venueId = this.#venueService.selectedVenueId();
    if (!venueId) {
      throw new Error('No venue selected');
    }

    // Use the PATCH endpoint which upserts if not exists
    const updatedIntegration = await this.#apiService.patch(`venues/${venueId}/${INTEGRATIONS_PATH}/${integration.integrationId}`, {
      isEnabled: !integration.isEnabled,
    } as unknown as IntegrationModelRequestDto);

    // Update the local state
    this.#enabledIntegrationsSource.update((list) => {
      const existingIndex = list.findIndex((i) => i.integrationId === updatedIntegration.integrationId);
      if (existingIndex >= 0) {
        // Update existing
        return list.map((i) => (i.integrationId === updatedIntegration.integrationId ? updatedIntegration : i));
      } else {
        // Add new
        return [...list, updatedIntegration];
      }
    });

    return updatedIntegration;
  }

  /**
   * Update an integration's settings
   * Uses PATCH /venues/:venueId/integrations/:integrationId which upserts
   * @see docs/INTEGRATIONS_BACKEND_IMPLEMENTATION.md for API contract
   */
  async updateIntegrationSettings(integrationIdOrMongoId: string, settings: Record<string, unknown>): Promise<IntegrationModelResponseDto> {
    const currentIntegrations = this.integrations();
    const integration = currentIntegrations.find((i) => i._id === integrationIdOrMongoId || i.integrationId === integrationIdOrMongoId);

    if (!integration) {
      throw new Error(`Integration with id ${integrationIdOrMongoId} not found`);
    }

    const venueId = this.#venueService.selectedVenueId();
    if (!venueId) {
      throw new Error('No venue selected');
    }

    // Use the venue-based PATCH endpoint which upserts
    const updatedIntegration = await this.#apiService.patch(`venues/${venueId}/${INTEGRATIONS_PATH}/${integration.integrationId}`, {
      settings,
    } as unknown as IntegrationModelRequestDto);

    // Update the local state
    this.#enabledIntegrationsSource.update((list) => {
      const existingIndex = list.findIndex((i) => i.integrationId === updatedIntegration.integrationId);
      if (existingIndex >= 0) {
        return list.map((i) => (i.integrationId === updatedIntegration.integrationId ? updatedIntegration : i));
      } else {
        return [...list, updatedIntegration];
      }
    });

    return updatedIntegration;
  }

  /**
   * Refresh integrations (alias for loadIntegrations)
   */
  refreshIntegrations(): void {
    this.loadIntegrations();
  }
}
